<template>
  <div class="goods-list" :class="cssType">
    <div class="section-head">
      <img v-if="designTitle" class="design-title" :src="designTitle" :alt="$t('novShopping.plaza')" />
      <template v-else>
        <img class="part-watermark" :src="partWatermarkImg" alt="" />
        <div class="title-row">
          <img class="gift" :src="giftImg" alt="" />
          <img v-if="mainTitleImg" class="main-title" :class="locale" :src="mainTitleImg" alt="title" />
        </div>
      </template>
    </div>

    <div class="desc">
      <i18n-t keypath="novShopping.desc" tag="span">
        <template #clickHere>
          <span class="link" @click="$emit('top-up')">{{ $t('novShopping.clickHere') }}</span>
        </template>
      </i18n-t>
    </div>

    <div class="event-time">{{ $t('novShopping.eventTime') }}</div>

    <div class="toolbar">
      <div class="remaining">
        {{ $t('novShopping.remainingLabel') }}
        <span class="remaining-num">{{ props.voucherBalance }}</span>
      </div>
      <div class="toolbar-actions">
        <div class="action-btn daily" @click="state.dailyDialogVisible = true">
          {{ $t('novShopping.goods.viewDaily') }}
        </div>
        <div class="action-btn history" @click="$emit('open-record')">
          {{ $t('novShopping.goods.history') }}
        </div>
      </div>
    </div>

    <div class="goods-grid">
      <GoodsCard
        v-for="goods in props.list"
        :key="goods.goodsCode"
        :goods="goods"
        :status="statusOf(goods)"
        :voucher-balance="props.voucherBalance"
        :logged-in="props.loggedIn"
        :activity-phase="props.activityPhase"
        @exchange="handleExchangeClick"
      />
    </div>

    <!-- 兑换确认弹窗 -->
    <Dialog v-model="state.confirmVisible" :title="$t('novShopping.goods.hotDeals')">
      <div class="confirm-dialog">
        <i18n-t keypath="novShopping.goods.confirm" tag="div" class="confirm-text">
          <template #n><span class="confirm-value">{{ confirmCost }}</span></template>
          <template #name><span class="confirm-value">{{ confirmName }}</span></template>
        </i18n-t>
        <div class="count-row" v-if="canAdjustCount">
          <div class="count-btn count-btn--step" @click="decCount">-</div>
          <div class="count-value">{{ state.count }}</div>
          <button
            type="button"
            class="count-btn count-btn--step"
            :disabled="state.count >= maxCount"
            @click="state.count = Math.min(maxCount, state.count + 1)"
          >+</button>
          <div class="count-btn" @click="fillMax">{{ $t('novShopping.goods.max') }}</div>
        </div>
        <div class="dialog-actions">
          <div class="action-btn plain" @click="state.confirmVisible = false">
            {{ $t('novShopping.common.cancel') }}
          </div>
          <div class="action-btn primary" @click="handleConfirm">
            {{ $t('novShopping.common.confirm') }}
          </div>
        </div>
      </div>
    </Dialog>

    <DailyRewardsDialog
      v-model="state.dailyDialogVisible"
      :list="props.dailyList"
    />
  </div>
</template>

<script setup>
import { computed, reactive, watch, defineProps, defineEmits } from 'vue'
import { useI18n } from 'vue-i18n'
import { isMobile } from '@/utils'
import { getPropName } from '../../utils/prop'
import { remainingQuantity, maxPurchaseQuantity } from '../../utils/activityState'
import partWatermarkImg from '../../assets/imgs/goods/part2-watermark.png'
import giftImg from '../../assets/imgs/goods/gift.png'
import mainTitleImg from '../../assets/imgs/goods/mainTitle/index.js'
import { goodsTitle as designTitle } from '../../assets/imgs/sectionTitles'
import Dialog from '../Dialog/index.vue'
import GoodsCard from '../GoodsCard/index.vue'
import DailyRewardsDialog from '../DailyRewardsDialog/index.vue'

const props = defineProps({
  activityPhase: { type: String, default: 'loading' },
  // 商品网格：今日每日商品 + 常驻商品
  list: {
    type: Array,
    default: () => [],
  },
  // 整期每日商品（查看每日奖励）
  dailyList: {
    type: Array,
    default: () => [],
  },
  // { [goodsCode]: { purchasedCount, todayCount } }
  statusMap: {
    type: Object,
    default: () => ({}),
  },
  voucherBalance: {
    type: Number,
    default: 0,
  },
  loggedIn: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['exchange', 'open-record', 'top-up', 'login-required', 'not-enough'])

const { t, locale } = useI18n()
const cssType = isMobile ? 'mobile' : 'pc'

const state = reactive({
  confirmVisible: false,
  dailyDialogVisible: false,
  goods: null,
  count: 1,
})

const statusOf = (goods) => props.statusMap[goods.goodsCode] || { purchasedCount: 0, todayCount: 0 }

const remainOf = (goods) => remainingQuantity(goods, statusOf(goods))
const maxCount = computed(() => state.goods ? maxPurchaseQuantity(state.goods, statusOf(state.goods), props.voucherBalance) : 0)
watch(maxCount, (maximum) => { state.count = Math.max(1, Math.min(state.count, maximum)) })
watch(() => props.activityPhase, (phase) => {
  if (phase !== 'active') state.confirmVisible = false
})

// 仅可购买 1 次时不展示数量调整
const canAdjustCount = computed(() => state.goods && remainOf(state.goods) > 1)

const confirmCost = computed(() => (state.goods ? Number(state.goods.price) * state.count : 0))

const confirmName = computed(() => {
  if (!state.goods) return ''
  return `${getPropName(t, state.goods)}*${Number(state.goods.propCount) * state.count}`
})

const handleExchangeClick = (goods) => {
  if (props.activityPhase !== 'active' || remainOf(goods) <= 0) return
  if (!props.loggedIn) {
    emit('login-required')
    return
  }
  if (props.voucherBalance < Number(goods.price)) {
    emit('not-enough')
    return
  }
  state.goods = goods
  state.count = 1
  state.dailyDialogVisible = false
  state.confirmVisible = true
}

const decCount = () => {
  if (state.count > 1) state.count -= 1
}

const fillMax = () => {
  state.count = maxCount.value
}

const handleConfirm = () => {
  if (!state.goods || props.activityPhase !== 'active') return
  if (!props.loggedIn) { state.confirmVisible = false; emit('login-required'); return }
  if (maxCount.value < 1) {
    state.confirmVisible = false
    if (props.voucherBalance < Number(state.goods.price)) emit('not-enough')
    return
  }
  state.count = Math.max(1, Math.min(Math.floor(state.count), maxCount.value))
  const payload = { goods: state.goods, count: state.count }
  state.confirmVisible = false
  emit('exchange', payload)
}
</script>

<style lang="less" scoped src="./pc_style.less"></style>
<style lang="less" scoped src="./mobile_style.less"></style>
